const defaultEmbedScriptLoadTimeout = 2000;

interface AppendHtmlOptions {
  timeOut?: number;
  signal?: AbortSignal;
}

export async function appendHtml(
  html: string,
  container: HTMLElement,
  { timeOut = defaultEmbedScriptLoadTimeout, signal }: AppendHtmlOptions = {}
): Promise<void> {
  const htmlParts = html
    .split(/(<script[\s\S]*?<\/script>)/)
    .filter((htmlPart) => htmlPart !== "");
  for (const htmlPart of htmlParts) {
    if (signal?.aborted) return;
    await appendEmbedPart(htmlPart, container, timeOut);
  }
}

async function appendEmbedPart(
  embedPart: string,
  container: HTMLElement,
  timeOut: number
): Promise<void> {
  return isScript(embedPart)
    ? await appendScript(embedPart, container, timeOut)
    : appendNonScriptHtml(embedPart, container);
}

function isScript(embedPart: string): boolean {
  return /^<script/.test(embedPart);
}

function appendNonScriptHtml(text: string, container: HTMLElement): void {
  const elements = htmlStringToElements(text);
  while (elements.length) {
    const node = elements[0];
    if (node) container.appendChild(node);
  }
}

async function appendScript(
  scriptAsText: string,
  container: HTMLElement,
  timeOut: number
): Promise<void> {
  const scriptNode = getScriptNodeFromHtmlString(scriptAsText);
  container.appendChild(scriptNode);
  if (scriptNode.hasAttribute("src") && !scriptNode.hasAttribute("async")) {
    await waitForScriptLoaded(scriptNode, timeOut);
  }
}

function waitForScriptLoaded(
  scriptNode: HTMLScriptElement,
  timeOut: number
): Promise<void> {
  return new Promise((resolve, reject) => {
    const loadFailTimeout = window.setTimeout(
      () => reject(new Error("Timed out after " + timeOut + "ms")),
      timeOut
    );
    scriptNode.onload = () => {
      window.clearTimeout(loadFailTimeout);
      resolve();
    };
    scriptNode.onerror = (error) => {
      window.clearTimeout(loadFailTimeout);
      reject(error);
    };
  });
}

const NON_EXECUTABLE_SCRIPT_TYPES = [
  "importmap",
  "text/javascript",
  "application/json",
];

function scriptTypeIsClassicInlineJavaScript(
  scriptEl: HTMLScriptElement
): boolean {
  const typeRaw = scriptEl.getAttribute("type");
  if (typeRaw === null) {
    return true;
  }
  const trimmed = typeRaw.trim();
  if (trimmed === "") {
    return true;
  }
  const essence = trimmed.split(";", 1)[0]?.trim().toLowerCase() ?? "";
  if (NON_EXECUTABLE_SCRIPT_TYPES.includes(essence)) {
    return false;
  }
  return essence === "application/javascript";
}

function getScriptNodeFromHtmlString(htmlString: string): HTMLScriptElement {
  const nonExecutableScriptNode = htmlStringToElements(
    htmlString
  )[0] as HTMLScriptElement;
  const executableScriptNode = document.createElement("script");
  for (const attr of nonExecutableScriptNode.attributes) {
    executableScriptNode.setAttribute(attr.name, attr.value);
  }
  const scriptText = nonExecutableScriptNode.text;
  const wrapAsIife =
    scriptTypeIsClassicInlineJavaScript(nonExecutableScriptNode) &&
    scriptText.trim() !== "";
  

  // Ensure inline scripts are executed in a function to avoid global scope pollution
  // Or repeated `const` declarations which will throw an error.
  executableScriptNode.text = wrapAsIife
    ? `(function(){\n${scriptText}\n})();`
    : scriptText;
  return executableScriptNode;
}

function htmlStringToElements(html: string): NodeListOf<ChildNode> {
  const template = document.createElement("template");
  template.innerHTML = html;
  return template.content.childNodes;
}
